import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { usePreferencesMutation } from '../api/preferences/updatePreferences';
import { PREFERENCE_MESSAGES } from '../pages/Preferences/constants';
import { useSnackbarsContext } from '../store/SnackbarProvider';
import { useWelcomeQuery } from '../api/getWelcomeDetails';
import { useBillsQuery } from '../api/getBills';
import { usePoliciesQuery } from '../api/getPolicies';
import {
  usePreferencesQuery,
  type GetPreferencesResponse,
} from '../api/preferences/getPreferences';
import {
  ExistingPreference,
  Notification,
  PolicyPreferences,
  PreferenceType,
} from '@aaa-ncnu-ie/digital-api-customer-prefs';

export const preferencesUpdateSuccessKey = 'preferences-update-success';

type PreferencesMutationType = 'paperless' | 'text' | 'update';

interface BaseUpdatePreferencesArgs {
  type: PreferencesMutationType;
  enrolled: boolean;
  isBillingAndTextIneligiblePolicies?: string[];
  billingAndPaymentEnabled?: boolean;
  policyDocumentsEnabled?: boolean;
  email?: string;
  phone?: string;
  pendingOnly?: boolean;
  onSuccess?: (response: GetPreferencesResponse) => void;
  onError?: (error: unknown) => void;
}

interface SinglePolicyUpdate extends BaseUpdatePreferencesArgs {
  bulk?: false;
  policyNumber: string;
}

interface BulkPolicyUpdate extends BaseUpdatePreferencesArgs {
  bulk: true;
  policyNumber?: never;
}

type UpdatePreferencesType = SinglePolicyUpdate | BulkPolicyUpdate;

interface GetUpdatedSelectionsArgs extends BaseUpdatePreferencesArgs {
  policyNumber?: string;
  currentSelections: UserSelection[];
}

interface UseUpdatePreferencesHook {
  updatePreferences: (args: UpdatePreferencesType) => void;
  isLoading: boolean;
}

export const isEnrolled = (
  selection = {} as UserSelection,
  type: 'email' | 'text' = 'email'
) => {
  if (type === 'email') {
    return [
      'policyDocuments',
      'billingAndPayment',
      'paymentReminders',
      'paymentConfirmation',
    ].some((item) => selection[item] === 'E');
  } else {
    return selection.textAlert === 'E';
  }
};

export const isPending = (selection = {} as UserSelection) =>
  [
    'policyDocuments',
    'billingAndPayment',
    'paymentReminders',
    'paymentConfirmation',
  ].some((item) => selection[item] === 'P');

const isEnrollOrPending = (
  selection: UpdatedUserSelection,
  item:
    | 'billingAndPayment'
    | 'paymentReminders'
    | 'paymentConfirmation'
    | 'policyDocuments'
) => ['E', 'P'].includes(selection[item] as string);

export const isEnrolledOrPendingBillingAnPayment = (
  selection = {} as UpdatedUserSelection
) =>
  ['billingAndPayment', 'paymentReminders', 'paymentConfirmation'].some(
    (item) =>
      isEnrollOrPending(
        selection,
        item as 'billingAndPayment' | 'paymentReminders' | 'paymentConfirmation'
      )
  );

export const areAnyEnrolledOrPending = (
  selection = {} as UpdatedUserSelection
) =>
  isEnrollOrPending(selection, 'policyDocuments') ||
  isEnrolledOrPendingBillingAnPayment(selection);

const updatePreferenceAction = (
  preferenceType: PreferenceType,
  enrolled: boolean
) => {
  preferenceType.preferenceAction = enrolled ? 'OPT_IN' : 'OPT_OUT';
};

const updateEmailNotificationPreferences = (
  policyPreference: PolicyPreferences,
  enrolled: boolean
) => {
  const emailNotification = policyPreference.notification.find(
    (notification: Notification) => notification.channelType === 'EMAIL'
  );
  emailNotification?.preferenceTypes.forEach((preferenceType: PreferenceType) =>
    updatePreferenceAction(preferenceType, enrolled)
  );
};

const updateExistingPreference = (
  existingPreference: ExistingPreference,
  enrolled: boolean
) => {
  existingPreference.policyPreferences.forEach(
    (policyPreference: PolicyPreferences) =>
      updateEmailNotificationPreferences(policyPreference, enrolled)
  );
};

// exported for testing purposes only
export const getUpdatedSelections = ({
  email,
  phone,
  enrolled,
  type,
  currentSelections,
  isBillingAndTextIneligiblePolicies,
  billingAndPaymentEnabled,
  policyDocumentsEnabled,
  policyNumber,
  pendingOnly,
}: GetUpdatedSelectionsArgs): UpdatedUserSelection[] => {
  let updatedSelections = currentSelections?.map((selection) => {
    const selectionIsPending = isPending(selection);
    const newSelection: UpdatedUserSelection = {
      policyNumber: selection.policyNumber,
      email: selection.email,
      phone: selection.phone,
    };

    if (selectionIsPending) {
      if (email) {
        newSelection.email ||= email;
      }
      if (phone) {
        newSelection.phone ||= phone;
      }
    } else {
      if (email) {
        newSelection.email = email;
      }
      if (phone) {
        newSelection.phone = phone;
      }
    }

    if (selection.textAlert === true) {
      newSelection.textAlert = selection.textAlert;
    }

    return newSelection;
  });

  if (policyNumber) {
    updatedSelections = updatedSelections?.filter(
      (selection) => selection.policyNumber === policyNumber
    );
  }

  if (pendingOnly) {
    const pendingPolicyNumbers = currentSelections
      ?.filter((selection) => isPending(selection))
      .map((selection) => selection.policyNumber);
    updatedSelections = updatedSelections?.filter((selection) =>
      pendingPolicyNumbers?.includes(selection.policyNumber)
    );
  }

  if (type === 'text') {
    updatedSelections = updatedSelections?.map((selection) => {
      selection.textAlert = enrolled ?? false;

      return selection;
    });
  } else {
    updatedSelections = updatedSelections?.map((selection) => {
      const isBillingEnrolled = () => {
        if (
          isBillingAndTextIneligiblePolicies?.includes(selection.policyNumber)
        )
          return false;

        return (
          billingAndPaymentEnabled ??
          enrolled ??
          isEnrolledOrPendingBillingAnPayment(selection)
        );
      };

      const isPolicyEnrolled = isEnrollOrPending(selection, 'policyDocuments');

      selection.policyDocuments =
        policyDocumentsEnabled ?? enrolled ?? isPolicyEnrolled;
      selection.billingAndPayment = isBillingEnrolled();
      selection.paymentReminders = isBillingEnrolled();
      selection.paymentConfirmation = isBillingEnrolled();

      return selection;
    });
  }

  return updatedSelections ?? [];
};

const useUpdatePreferences = (): UseUpdatePreferencesHook => {
  const queryClient = useQueryClient();
  const updatePreferencesMutation = usePreferencesMutation();
  const { enqueueSnackbar } = useSnackbarsContext();
  const { data: welcomeData } = useWelcomeQuery();
  const { data: billsData } = useBillsQuery(!!welcomeData);
  const authToken = welcomeData?.authToken ?? '';
  const { data: policiesData } = usePoliciesQuery(!!welcomeData);

  // Only use policies that are available in both the /bills (CDH) and /policies (GPD) responses
  const verifiedPolicies = policiesData?.policies.filter((policy) =>
    billsData?.payments.find(
      (payment) => payment.policyNumber === policy.policyNumber
    )
  );
  const { data: preferencesData } = usePreferencesQuery(
    authToken,
    verifiedPolicies?.map((policy) => ({
      policyNumber: policy.policyNumber,
      policySourceSystem: policy.dataSource,
    }))
  );

  const updatePreferences = useCallback(
    (updatePreferencesArgs: UpdatePreferencesType) => {
      const { type, enrolled, pendingOnly, onSuccess, onError } =
        updatePreferencesArgs;
      const policyNumber =
        'policyNumber' in updatePreferencesArgs
          ? updatePreferencesArgs.policyNumber
          : undefined;

      // add the email/phone to each selection if it is passed in
      const updatedSelections = getUpdatedSelections({
        ...updatePreferencesArgs,
        policyNumber,
        currentSelections: preferencesData?.selections ?? [],
      });

      let updatedPreferences = preferencesData?.existingPreferences;
      if (updatedPreferences) {
        if (policyNumber) {
          updatedPreferences = updatedPreferences.filter(
            (preference) => preference.policyNumber === policyNumber
          );
        }

        if (pendingOnly) {
          const pendingPolicyNumbers = preferencesData?.selections
            ?.filter((selection) => isPending(selection))
            .map((selection) => selection.policyNumber);
          updatedPreferences = updatedPreferences.filter((preference) =>
            pendingPolicyNumbers?.includes(preference.policyNumber)
          );
        }

        if (type !== 'text') {
          updatedPreferences?.forEach((existingPreference) => {
            const shouldUpdatePreference =
              !policyNumber || existingPreference.policyNumber === policyNumber;
            if (shouldUpdatePreference) {
              updateExistingPreference(existingPreference, enrolled);
            }
          });
        }

        const policies: { [key: string]: string } = {};
        preferencesData?.selections.forEach((selection) => {
          const foundPolicy = policiesData?.policies.find(
            (policy) => policy.policyNumber === selection.policyNumber
          );
          policies[selection.policyNumber as string] =
            foundPolicy?.dataSource ?? '';
        });

        const preferencesRequest = {
          authToken: welcomeData?.authToken ?? '',
          existingPreferences: updatedPreferences,
          selections: updatedSelections,
          policies: JSON.stringify(policies),
          customerid: welcomeData?.userInfo?.custKey ?? '',
        };

        updatePreferencesMutation.mutateAsync(preferencesRequest, {
          onSuccess: async (response) => {
            if (onSuccess) onSuccess(response);
            await queryClient.invalidateQueries({
              queryKey: ['preferences'],
            });
            enqueueSnackbar({
              variant: 'success',
              key: preferencesUpdateSuccessKey,
              autoHideDuration: 10000,
              title:
                PREFERENCE_MESSAGES.success[type][
                  enrolled ? 'enrolled' : 'unenrolled'
                ],
            });
            window.scrollTo(0, 0);
          },
          onError: (error) => {
            if (onError) onError(error);
            enqueueSnackbar({
              variant: 'error',
              key: 'preferences-update-error',
              title: PREFERENCE_MESSAGES.error.title,
              content: PREFERENCE_MESSAGES.error.message,
            });
            window.scrollTo(0, 0);
          },
        });
      }
    },
    [welcomeData, policiesData, billsData, preferencesData]
  );

  return {
    updatePreferences,
    isLoading: updatePreferencesMutation.isPending,
  };
};

export default useUpdatePreferences;
